<script setup lang="ts">
import {
  UIButton,
  UIDropdown,
  renderIcon,
  useNotification,
} from '@gohighlevel/ghl-ui'
import {
  DotsVerticalIcon,
  Download01Icon,
  SlashCircle01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { useDocument, useMobileView } from '@/composable'
import { useI18n } from 'vue-i18n'
import {
  DeclineModal,
  DocumentStatus,
  UpdateSignatureParams,
} from '@gohighlevel/ghl-proposals-common'
import { useDocumentStore } from '@/store/document'
import { computed, inject, ref } from 'vue'
import { router } from '@/router'
import { useRoute } from 'vue-router'
import { DocumentServices } from '@/service/DocumentService'

const { t } = useI18n()
const store = useDocumentStore()
const route = useRoute()
const isPreview = inject<boolean>('isPreview')
const notification = useNotification()
const { loading, downloadProposalPdf, loadingDownloadPdf } = useDocument()
const { isMobile } = useMobileView()
const isCCrecipient = computed(() => store.isCcRecipient)

const showDeclineModal = ref(false)
const declineLoading = ref(false)

const onSelect = async (value: string) => {
  if (value === 'download') {
    await downloadProposalPdf()
  } else if (value === 'decline') {
    showDeclineModal.value = true
  }
}

const handleDecline = async (data: {
  reasons: string[]
  reasonBody: string
}) => {
  try {
    declineLoading.value = true
    const { data: response } = await DocumentServices.acceptDocument({
      documentId: store.document._id,
      recipientId: store.activeRecipient?.id,
      declined: true,
      declinedReason: data.reasons,
      declinedReasonBody: data.reasonBody,
      signature: '',
      signedDate: new Date().toISOString(),
      fillableFields: store.getFillableFields(),
      referenceId: route?.params?.referenceId || '',
    } as UpdateSignatureParams)

    if (response.success) {
      store.setDocumentStatus(DocumentStatus.DECLINED)
      showDeclineModal.value = false
      router.push({
        name: 'decline-success',
        params: { documentId: store.document._id },
      })
    }
  } catch (error) {
    console.error('Error declining document:', error)
    notification.error({
      title: t('common.error'),
      content: t('documentViewer.errorDecliningDocument'),
    })
  } finally {
    declineLoading.value = false
  }
}

const options = [
  {
    label: t('documentViewer.downloadPdf'),
    key: 'download',
    icon: renderIcon(Download01Icon),
  },
]

// Show decline option if document is in SENT or VIEWED status
if (
  [DocumentStatus.SENT, DocumentStatus.VIEWED].includes(store.status) &&
  !isPreview &&
  store.hasPrevRecipientsCompleted &&
  !isCCrecipient.value
) {
  options.push({
    label: t('documentViewer.declineDocument'),
    key: 'decline',
    icon: renderIcon(SlashCircle01Icon),
  })
}
</script>

<template>
  <UIButton
    :loading="loadingDownloadPdf"
    id="document-state-actions-btn"
    size="small"
    variant="primary"
    :class="[
      'shadow-lg !text-gray-900',
      'cursor-pointer',
      { 'ml-1': !isMobile },
    ]"
    type="tertiary"
    @click="onSelect('download')"
    :disabled="loading || loadingDownloadPdf"
    v-if="options.length <= 1"
  >
    <template v-if="!loadingDownloadPdf">
      <Download01Icon class="h-3.5 w-3.5" :class="{ 'mr-2': !isMobile }" />
      <UITextSmRegular v-if="!isMobile">
        {{ t('documentViewer.downloadPdf') }}
      </UITextSmRegular>
    </template>
    <template v-else>
      <UITextSmRegular v-if="!isMobile">
        {{ t('common.downloading') }}...
      </UITextSmRegular>
    </template>
  </UIButton>
  <DeclineModal
    v-model:show="showDeclineModal"
    :loading="declineLoading"
    @onSubmit="handleDecline"
  />

  <UIDropdown
    :options="options"
    trigger="click"
    @select="onSelect"
    :id="'document-actions-dropdown'"
    v-if="options.length > 1"
  >
    <UIButton
      :loading="loading"
      id="document-state-actions-btn"
      size="small"
      variant="primary"
      :class="['shadow-lg !text-gray-900', { 'ml-1': !isMobile }]"
      type="tertiary"
    >
      <template v-if="!loading">
        <DotsVerticalIcon class="h-3.5 w-3.5" />
      </template>
    </UIButton>
  </UIDropdown>
</template>
